import { useState } from 'react';
import {
  CpuChipIcon, CircleStackIcon, ServerStackIcon, CheckCircleIcon,
  ExclamationTriangleIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useResources } from '../hooks/useResources';

// Plain-English "what does this app do" for a non-technical reader (directors).
const APP_INFO = {
  'dashboard-server': { title: 'Dashboard', desc: 'The home page everyone lands on after signing in — it routes staff to every other tool and serves the company directory.' },
  'tech-server': { title: 'IT & Infosec', desc: 'Behind-the-scenes IT tools: staff directory, Microsoft 365 admin, leaver off-boarding, access permissions and the ISO 27001 tracker.' },
  'inspections-server': { title: 'Health & Safety', desc: 'Engineer safety inspections — ladders, harnesses, vehicles and audits — and the PDF reports and certificates they produce.' },
  'logistics-server': { title: 'Logistics', desc: 'Stock and inventory — ISP stock counts and warehouse management.' },
  'fsm-server': { title: 'Field Service', desc: 'The app engineers open on site to reach stock counts and safety inspections.' },
  'training-server': { title: 'Training', desc: 'Staff training — course management, completions and certificates.' },
};

// One fixed colour per METRIC — purple is always CPU, blue is always memory.
const CPU_COLOR = '#711C91';
const RAM_COLOR = '#0072B2';

function infoFor(app) {
  return APP_INFO[app] || { title: app.replace(/-server$/, '').replace(/\b\w/g, (c) => c.toUpperCase()), desc: '' };
}
function fmtMB(bytes) {
  return `${Math.round(bytes / 1048576)} MB`;
}
function fmtGB(bytes) {
  return `${(bytes / 1073741824).toFixed(1)} GB`;
}
function fmtUptime(ms) {
  if (!ms) return '—';
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.max(1, Math.round(ms / 60000))}m`;
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
// Clock label from a position fraction of the day (0 → 00:00, 1 → 24:00).
// Position-based so it's timezone-agnostic: the axis IS one civil day.
function civilLabel(frac) {
  const h = Math.round(frac * 24);
  return `${String(h).padStart(2, '0')}:00`;
}
// 'YYYY-MM-DD' ± days, computed in UTC so it never drifts across a DST edge.
function shiftDay(d, delta) {
  const [y, m, day] = d.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, day));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}
function prettyDate(d) {
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

// ── Compact single-metric line chart on a fixed 00:00–24:00 day axis ────────
function MiniChart({ series, valueOf, color, fmtValue, xMin, xMax, label, yMaxFixed, yTickFmt }) {
  const [hi, setHi] = useState(null);
  const W = 1000;
  const H = 200;
  const padL = yMaxFixed != null ? 44 : 8;
  const padR = 8;
  const padT = 16;
  const padB = 26;

  const pts = series
    .map((p) => ({ t: p.t, v: valueOf(p) }))
    .filter((p) => p.t >= xMin && p.t <= xMax)
    .sort((a, b) => a.t - b.t);

  const x = (t) => padL + ((W - padL - padR) * (t - xMin)) / (xMax - xMin || 1);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  let vMax;
  if (yMaxFixed != null) {
    vMax = yMaxFixed;
  } else {
    vMax = 0;
    pts.forEach((p) => { vMax = Math.max(vMax, p.v); });
    vMax = vMax > 0 ? vMax * 1.2 : 1;
  }
  const y = (v) => padT + (H - padT - padB) * (1 - Math.min(v, vMax) / vMax);
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  const hp = hi != null ? pts[hi] : null;
  const last = pts[pts.length - 1];
  const gid = `g-${label}-${color.replace('#', '')}`;

  return (
    <div className="relative">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
        <span className="text-sm font-semibold [font-variant-numeric:tabular-nums]" style={{ color }}>
          {last ? fmtValue((hp || last).v) : '—'}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={`${label} across the day`}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* fixed y-axis reference lines + labels (only when the scale is fixed, e.g. CPU 0–100%) */}
        {yMaxFixed != null && yTicks.map((f) => (
          <g key={`y${f}`}>
            <line x1={padL} x2={W - padR} y1={y(yMaxFixed * f)} y2={y(yMaxFixed * f)} stroke="#f1f3f5" strokeWidth="1" />
            <text x={padL - 6} y={y(yMaxFixed * f) + 4} textAnchor="end" fontSize="11" fill="#b0b6bd">
              {yTickFmt ? yTickFmt(yMaxFixed * f) : Math.round(yMaxFixed * f)}
            </text>
          </g>
        ))}
        {/* fixed day gridlines + hour labels */}
        {ticks.map((f) => (
          <g key={f}>
            <line x1={padL + (W - padL - padR) * f} x2={padL + (W - padL - padR) * f} y1={padT} y2={H - padB} stroke="#f1f3f5" strokeWidth="1" />
            <text x={padL + (W - padL - padR) * f} y={H - 8} textAnchor="middle" fontSize="11" fill="#b0b6bd">{civilLabel(f)}</text>
          </g>
        ))}
        {pts.length >= 2 && (
          <>
            <polygon points={`${x(pts[0].t)},${H - padB} ${pts.map((p) => `${x(p.t)},${y(p.v)}`).join(' ')} ${x(last.t)},${H - padB}`} fill={`url(#${gid})`} />
            <polyline points={pts.map((p) => `${x(p.t)},${y(p.v)}`).join(' ')} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          </>
        )}
        {hp && <line x1={x(hp.t)} x2={x(hp.t)} y1={padT} y2={H - padB} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" />}
        {(hp || last) && <circle cx={x((hp || last).t)} cy={y((hp || last).v)} r="4" fill={color} stroke="#fff" strokeWidth="1.5" />}
        {pts.map((p, idx) => (
          <rect
            key={p.t}
            x={idx === 0 ? padL : (x(pts[idx - 1].t) + x(p.t)) / 2}
            width={Math.max(2, (W - padL - padR) / Math.max(pts.length, 1))}
            y={padT}
            height={H - padT - padB}
            fill="transparent"
            onMouseEnter={() => setHi(idx)}
            onMouseLeave={() => setHi(null)}
          />
        ))}
      </svg>
      {pts.length < 2 && (
        <p className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-xs text-gray-400">
          No data for this day yet
        </p>
      )}
      {hp && (
        <p className="mt-0.5 text-center text-[10px] text-gray-400">
          {civilLabel((hp.t - xMin) / (xMax - xMin))} · {fmtValue(hp.v)}
        </p>
      )}
    </div>
  );
}

// Status-page-style 30-day availability strip.
const DAY_STATE = { up: 'bg-emerald-400', partial: 'bg-amber-400', down: 'bg-rose-500', none: 'bg-gray-200' };
const DAY_LABEL = { up: 'up', partial: 'partial outage', down: 'down', none: 'no data' };

function UptimeStrip({ health }) {
  if (!health) return null;
  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Availability (30 days)</span>
        <span className="text-sm font-semibold text-gray-700 [font-variant-numeric:tabular-nums]">
          {health.uptimePct != null ? `${health.uptimePct}%` : 'building…'}
        </span>
      </div>
      <div className="flex gap-px">
        {health.days.map((d) => (
          <span
            key={d.date}
            title={`${prettyDate(d.date)}: ${DAY_LABEL[d.state]}${d.pct != null && d.state !== 'up' ? ` (${d.pct}% up)` : ''}`}
            className={`h-4 flex-1 rounded-[1px] ${DAY_STATE[d.state]}`}
          />
        ))}
      </div>
    </div>
  );
}

function StabilityBadge({ status, crashes }) {
  if (status && status !== 'online') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800"><ExclamationTriangleIcon className="h-3.5 w-3.5" /> {status}</span>;
  }
  if (crashes > 0) {
    return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800"><ExclamationTriangleIcon className="h-3.5 w-3.5" /> Crash-looping</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"><CheckCircleIcon className="h-3.5 w-3.5" /> Stable</span>;
}

function AppCard({ app, xMin, xMax, ramMax }) {
  const info = infoFor(app.app);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-1 flex items-start justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">{info.title}</h2>
        <StabilityBadge status={app.status} crashes={app.crashes} />
      </div>
      {info.desc && <p className="mb-3 text-sm text-gray-500">{info.desc}</p>}

      <div className="grid grid-cols-1 gap-4">
        <MiniChart series={app.series} valueOf={(p) => p.cpu} color={CPU_COLOR} fmtValue={(v) => `${v.toFixed(1)}%`} xMin={xMin} xMax={xMax} label="CPU" yMaxFixed={100} yTickFmt={(v) => `${v}%`} />
        <MiniChart series={app.series} valueOf={(p) => p.ram} color={RAM_COLOR} fmtValue={fmtMB} xMin={xMin} xMax={xMax} label="Memory" yMaxFixed={ramMax} yTickFmt={(v) => `${Math.round(v / 1073741824)} GB`} />
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500 [font-variant-numeric:tabular-nums]">
        <span>Restarts (day): <span className={`font-medium ${app.restartsOnDay > 0 ? 'text-amber-700' : 'text-gray-700'}`}>{app.restartsOnDay}</span></span>
        <span>Crashes: <span className={`font-medium ${app.crashes > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{app.crashes}</span></span>
        <span>Up for: <span className="font-medium text-gray-700">{fmtUptime(app.uptimeMs)}</span></span>
      </div>

      <UptimeStrip health={app.health} />
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
      <span className="rounded-md bg-brand-light p-2"><Icon className="h-5 w-5 text-brand" /></span>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-lg font-semibold text-gray-900 [font-variant-numeric:tabular-nums]">{value}</p>
        {sub && <p className="text-xs text-gray-500">{sub}</p>}
      </div>
    </div>
  );
}

function Resources() {
  const [date, setDate] = useState(null); // null = today
  const { data, isLoading, isError, error } = useResources(date);

  if (isError) {
    const status = error?.response?.status;
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
          {status === 403 ? 'This page is for full administrators only.' : `Couldn’t load resource usage: ${error?.response?.data?.error || error.message}`}
        </div>
      </div>
    );
  }

  const sys = data?.system;
  const apps = data?.apps || [];
  const curDate = data?.date;
  const isToday = curDate && data?.today && curDate === data.today;
  const canPrev = curDate && data?.oldestDate && curDate > data.oldestDate;
  const canNext = curDate && data?.today && curDate < data.today;
  const totalRestarts = apps.reduce((s, a) => s + (a.restartsOnDay || 0), 0);
  const anyCrashing = apps.some((a) => a.crashes > 0 || (a.status && a.status !== 'online'));
  // Fix the RAM axis to the box's total memory (rounded up to a whole GB) so
  // each app's memory reads against real capacity — the same "share of the
  // machine" framing as CPU's 0–100%.
  const ramMax = sys ? Math.ceil(sys.memTotalBytes / 1073741824) * 1073741824 : null;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Resources</h1>
        {curDate && (
          <div className="flex items-center gap-1">
            <button type="button" disabled={!canPrev} onClick={() => setDate(shiftDay(curDate, -1))}
              className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent" aria-label="Previous day">
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="min-w-[7rem] text-center text-sm font-medium text-gray-700">{isToday ? 'Today' : prettyDate(curDate)}</span>
            <button type="button" disabled={!canNext} onClick={() => setDate(canNext && shiftDay(curDate, 1) === data.today ? null : shiftDay(curDate, 1))}
              className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-brand-light disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent" aria-label="Next day">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      <p className="mb-6 text-sm text-gray-500">
        How hard each app worked across the day (00:00–24:00) — processing power
        (CPU) and memory (RAM) — plus restarts, crashes and 30-day availability.
        Sampled every 2 minutes.
      </p>

      {isLoading && !data && <p className="text-sm text-gray-400">Loading…</p>}

      {sys && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatTile icon={CpuChipIcon} label="Processing power" value={`${sys.cores} cores`} sub={`load ${sys.load1.toFixed(2)} · ${sys.load5.toFixed(2)} · ${sys.load15.toFixed(2)}`} />
          <StatTile icon={CircleStackIcon} label="Memory used" value={`${fmtGB(sys.memUsedBytes)} / ${fmtGB(sys.memTotalBytes)}`} sub={`${fmtGB(sys.memAvailableBytes)} free`} />
          <StatTile icon={ServerStackIcon} label="Restarts (this day)" value={totalRestarts} sub={anyCrashing ? 'an app is unstable — see below' : 'all apps stable'} />
        </div>
      )}

      {sys?.disks?.length > 0 && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-700">Storage</h2>
          <div className="space-y-3">
            {sys.disks.map((d) => {
              const pct = d.totalBytes ? (d.usedBytes / d.totalBytes) * 100 : 0;
              const warn = pct >= 85;
              return (
                <div key={d.path}>
                  <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2 text-xs">
                    <span className="font-medium text-gray-700">{d.label} <span className="text-gray-400">{d.path}</span></span>
                    <span className={`[font-variant-numeric:tabular-nums] ${warn ? 'font-semibold text-rose-700' : 'text-gray-500'}`}>
                      {fmtGB(d.usedBytes)} / {fmtGB(d.totalBytes)} ({pct.toFixed(0)}%) · {fmtGB(d.freeBytes)} free
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className={`h-full rounded-full ${warn ? 'bg-rose-500' : 'bg-brand'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {apps.length === 0 && !isLoading && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No samples for this day.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {apps.map((a) => (
          <AppCard key={a.app} app={a} xMin={data.dayStartMs} xMax={data.dayEndMs} ramMax={ramMax} />
        ))}
      </div>

      {data?.generatedAt && (
        <p className="mt-4 text-xs text-gray-400">
          Updated {new Date(data.generatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}{isToday ? ' · refreshes every minute' : ''}
        </p>
      )}
    </div>
  );
}

export default Resources;
